import './ProductCategoryApp.less';
import React, {useState, useEffect} from 'react';
import ProductFilter from "../../widgets/product-filter/ProductFilter";
import {getUrlHash, setUrlHash} from "../../../utils/url-hash";
import Request from "../../../api/Request";
import ProductListParcel from "../../endpoints/product/ProductListParcel";
import PagingPanel from "../../../components/paging-panel/PagingPanel";
import RelationHelper from "../../../helpers/relation/RelationHelper";
import ProductCard from "../../widgets/product-card/ProductCard";
import SingleSelect from '../../../components/single-select/SingleSelect';
import LabelMap from '../../../types/label-map/LabelMap';
import __ from '../../../i18n';
import ProductAttributeRelationsLoader from '../../singleton/ProductAttributeRelationsLoader';
import getFilterableAttributeGroupsByProductClasses from '../../utils/product-attribute/getFilterableAttributeGroupsByProductClasses';
import getFilterableAttributes from "../../utils/product-attribute/getFilterableAttributes";
import parseGroupedAttributeIds from "../../utils/product-attribute/parseGroupedAttributeIds";


const sortingMap = new LabelMap([
    ['selling_well', __('Selling well'), {orders: [['priority', false], ['publish_time', false]]}],
    ['latest', __('Latest'), {orders: [['publish_time', false]]}],
    ['descending_price', __('Descending price'), {orders: [['sale_price', false]]}],
    ['ascending_price', __('Ascending price'), {orders: [['sale_price', true]]}],
]);


export default function ProductCategoryApp({categoryIds}) {
    const LISTING_STATE_KEY = 'listingState';

    const pageSize = 24;

    const getInitialListingState = () => {
        const defaultState = {
            groupedAttributeIds: {},
            priceRange: [null, null],
            sort: sortingMap.items[0].value,
            page: 0,
        };

        try {
            const urlState = JSON.parse(getUrlHash(LISTING_STATE_KEY));

            return Object.assign({}, defaultState, urlState);

        } catch (err) {

            return defaultState;
        }
    };

    const [listingState, setListingState] = useState(getInitialListingState());

    const [loadingState, setLoadingState] = useState({loading: false, error: null});

    const [data, setData] = useState({items: [], total: 0, relations: {}});

    const patchListingState = (values) => {
        setListingState(Object.assign({}, listingState, values));
    };

    const pushListingStateToUrlHash = () => {
        setUrlHash(LISTING_STATE_KEY, JSON.stringify(listingState));
    };

    const loadProducts = () => {
        setLoadingState({loading: true, error: null});

        const {
            brandIds,
            conditionLevels,
            productAttributeIdLists
        } = parseGroupedAttributeIds(listingState.groupedAttributeIds);

        const sorting = sortingMap.get(listingState.sort);
        const orders = sorting ? sorting.extra.orders : null;

        Request.add(new ProductListParcel(
            {
                categoryIds,
                brandIds,
                conditionLevels,
                productAttributeIdLists,
                priceRange: listingState.priceRange,
            },
            orders,
            listingState.page,
            pageSize,
        ))
            .then(data => {
                setData(data);
                setLoadingState({loading: false, error: null});
            })
            .catch(error => {
                setLoadingState({loading: false, error: error.getMessage()});
            });
    };

    useEffect(() => {
        pushListingStateToUrlHash();
        loadProducts();
    }, [listingState]);



    const [attributeRelations, setAttributeRelations] = useState(ProductAttributeRelationsLoader.getData());

    useEffect(() => {
        if (attributeRelations === null) {
            const listener = () => {
                setAttributeRelations(ProductAttributeRelationsLoader.getData());
            };
            ProductAttributeRelationsLoader.addListener(listener);
            ProductAttributeRelationsLoader.load();

            return () => ProductAttributeRelationsLoader.removeListener(listener);
        }
    }, []);

    const getFilterableAttributeGroups = () => {
        const productCategories = attributeRelations.product_category
            .filter(t => categoryIds.includes(t.id));

        return getFilterableAttributeGroupsByProductClasses(
            productCategories.map(t => t.product_class_id),
            attributeRelations
        );
    };

    const __getFilterableAttributes = () => {
        return getFilterableAttributes(attributeRelations);
    };

    const relationHelper = new RelationHelper(data.relations);

    const renderPageItemsLocationBlock = () => {
        const totalPages = data.total > 0 ? Math.ceil(data.total / pageSize) : 1;
        const itemsLocationBegin = listingState.page * pageSize + 1;
        const itemsLocationEnd = Math.min(listingState.page * pageSize + pageSize, data.total);

        if (itemsLocationBegin > itemsLocationEnd ) {
            return null;
        }

        return <div className="page-items-location">
            {
                __('Showing :begin - :end of :total', {
                    begin: itemsLocationBegin,
                    end: itemsLocationEnd,
                    total: data.total,
                })
            }
        </div>;
    };

    const renderProductFilter = () => {
        if (attributeRelations === null) {
            return null;
        }

        return <ProductFilter
            attributeGroups={getFilterableAttributeGroups()}
            attributes={__getFilterableAttributes()}
            defaultGroupedAttributeIds={listingState.groupedAttributeIds}
            defaultPriceRange={listingState.priceRange}
            onGroupedAttributeIdsChange={groupedAttributeIds => patchListingState({groupedAttributeIds})}
            onPriceRangeChange={priceRange => patchListingState({priceRange})}
        />;
    };

    return <div className="product-category-app">
        <div className="main-col">
            <div className="heading">
                <div className="left">
                    {
                        renderProductFilter()
                    }
                    {
                        renderPageItemsLocationBlock()
                    }
                </div>
                <div className="right">
                    <div className="sort-block">
                        <div className="label">{__('Order by')}</div>
                        <SingleSelect
                            appearance="box-accent"
                            options={sortingMap}
                            defaultValue={listingState.sort}
                            onChange={sort => patchListingState({sort})}
                        />
                    </div>
                </div>
            </div>
            <div className="grid-view clear-fix">
                {
                    data.items.length > 0 &&
                    <ul>
                        {
                            data.items.map(promoProduct => {
                                return <li key={promoProduct.id}>
                                    <ProductCard
                                        promoProduct={promoProduct}
                                        avatarImage={relationHelper.find(promoProduct, 'product', 'avatar_image->image')}
                                        hoverImage={relationHelper.find(promoProduct, 'product', 'hover_image->image')}
                                        brand={relationHelper.find(promoProduct, 'product', '->brand')}
                                    />
                                </li>;
                            })
                        }
                    </ul>
                }
            </div>
            <PagingPanel
                currentPage={listingState.page}
                pageSize={pageSize}
                totalItems={data.total}
                goTo={page => patchListingState({page})}
            />
        </div>
        <div className="left-col">
            {
                renderProductFilter()
            }
        </div>
    </div>;
}
